import localeEn from '@fullcalendar/core/locales/en-gb'
import localeFi from '@fullcalendar/core/locales/fi'
import localeNl from '@fullcalendar/core/locales/nl'
import localeSv from '@fullcalendar/core/locales/sv'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { calendarGetRange, calendarPatch } from '../../api'
import { PageContent } from '../../common/layout/common.js'
import LoadingCentered from '../../common/loaders/loadingcentered'
import { TimelineEvent } from '../../common/timelinevent/timelineevent.jsx'
import { useCurrentUser } from '../../hooks/useCurrentUser.ts'
import i18n from '../../i18n'
import { queryClient } from '../../main.jsx'
import { NewAbscenceModal } from './newabscencemodal/newabscencemodal'
import { CalendarCellDate, PersonalCalendarWrapper } from './personalcalendarstyled.jsx'
import { PersonalCalendarToolbar } from './personalcalendartoolbar.jsx'

const localeMap = {
	fi: localeFi,
	sv: localeSv,
	en: localeEn,
	nl: localeNl,
}

const queryKey = 'calendarEvents'

const minusOneDay = (dateStr) => DateTime.fromISO(dateStr).minus({ days: 1 }).toFormat('yyyy-MM-dd')

// https://fullcalendar.io/docs/premium
export const PersonalCalendar = () => {
	const currentUser = useCurrentUser()
	const [commonAndCompanyEvents, setCommonAndCompanyEvents] = useState([])

	const fetchEvents = async ({ queryKey }) => {
		const [_key, { start, end }] = queryKey
		const res = await calendarGetRange(start, end)
		if (res.status !== 200) throw new Error('Failed to load events')

		if (currentUser && res.data) {
			setCommonAndCompanyEvents(res.data.filter((event) => event.scope === 'COMMON' || event.scope === 'COMPANY'))
		}

		return (
			currentUser &&
			res.data
				.filter(({ personId, scope }) => personId === currentUser.personId || scope === 'COMMON' || scope === 'COMPANY')
				.map((row) => ({
					// TODO: different events by scope
					id: row.id,
					title: row.info,
					start: row.startDate,
					end: row.endDate, // exclusive
					resourceId: row.personId, // no resourceId → global event
					causeCode: row.causeCode,
					icdCode: row.icdCode,
					status: row.status,
					personId: row.personId,
					approvers: row.approvers,
					dayHours: row.dayHours,
					ownAnnouncement: row.ownAnnouncement,
					longAbsence: row.longAbsence,
					//color: "green",
					//className: "global-event",
					//display: "background",
					//allDay: true,
				}))
		)
	}

	const [event, setEvent] = useState()
	const calendarRef = useRef(null)
	const calendarApi = calendarRef?.current?.getApi()
	const calendarViewStartDate = DateTime.fromJSDate(calendarApi?.currentData?.dateProfile?.currentRange?.start)
	const calendarViewEndDate = DateTime.fromJSDate(calendarApi?.currentData?.dateProfile?.currentRange?.end)
	const [dateRange, setDateRange] = useState(null)
	const handleDatesSet = ({ startStr, endStr }) =>
		setDateRange({ start: startStr.slice(0, 10), end: endStr.slice(0, 10) })

	const getCompanyCommonHoliday = (cellDate) =>
		commonAndCompanyEvents.length > 0 &&
		commonAndCompanyEvents.find((event) => event.date === DateTime.fromJSDate(cellDate).toFormat('yyyy-MM-dd'))

	const {
		data: events = [],
		isLoading,
		error,
	} = useQuery({
		queryKey: [queryKey, dateRange],
		queryFn: fetchEvents,
		enabled: !!dateRange && !!currentUser,
	})

	/**
	 * @param {import('@fullcalendar/interaction').DateClickArg} info
	 */
	const handleDateClick = (info) => {
		/* handleDateClick should only be used to create new events, not to edit existing ones spanning multiple days */
		setEvent({
			start: info.dateStr,
			end: info.dateStr,
			allDay: true,
			personId: currentUser.personId,
			personNr: currentUser.nr,
			personName: currentUser.name,
			calendarApi,
			info: '',
			dayHours: undefined,
			ownAnnouncement: false,
			extendedProps: {},
		})
	}

	const handleDateRangeSelect = (info) => {
		const calendarApi = calendarRef.current.getApi()
		setEvent({
			start: info.startStr,
			end: minusOneDay(info.endStr),
			personId: currentUser.personId,
			personNr: currentUser.nr,
			personName: currentUser.name,
			calendarApi,
			info: '',
			dayHours: undefined,
			ownAnnouncement: false,
		})
	}

	/**
	 * @param {import('@fullcalendar/core').EventClickArg} info
	 */
	const handleEventClick = ({ event }) => {
		const json = event.toJSON()
		json.personId = currentUser.personId
		json.personNr = currentUser.nr
		json.personName = currentUser.name
		json.end = minusOneDay(json.end)
		json.causeCode = event.extendedProps.causeCode
		json.icdCode = event.extendedProps.icdCode
		json.status = event.extendedProps.status
		json.dayHours = event.extendedProps.dayHours
		json.info = event.title
		json.ownAnnouncement = event.extendedProps.ownAnnouncement
		json.longAbsence = event.extendedProps.longAbsence
		json.approvers = event.extendedProps.approvers
		setEvent(json)
	}

	const handleEventDropResize = async (info) => {
		const { event } = info

		try {
			await calendarPatch(event.id, {
				personId: currentUser.personId,
				startDate: event.startStr,
				endDate: minusOneDay(event.endStr),
				causeCode: event.extendedProps.causeCode,
				icdCode: event.extendedProps.icdCode,
				status: event.extendedProps.status,
				info: event.title,
				ownAnnouncement: event.ownAnnouncement,
			})
			queryClient.invalidateQueries({ queryKey: [queryKey] }) // refetch data
		} catch (error) {
			console.error('Failed to update event', error)
			info.revert() // revert the change if API call fails
		}
	}

	return (
		<>
			{isLoading && <LoadingCentered show />}
			{error && (
				<Alert variant="danger" className="text-center">
					{error.message}
				</Alert>
			)}
			{!!event && <NewAbscenceModal setIsOpen={setEvent} event={event} openedByDateClick={false} />}
			<PageContent>
				{calendarRef?.current && <PersonalCalendarToolbar calendarRef={calendarRef?.current} />}
				<PersonalCalendarWrapper className="ag-theme-material styles-ag flex-grow-1 w-100">
					<FullCalendar
						ref={calendarRef}
						height="contentHeight"
						aspectRatio={2.5}
						plugins={[dayGridPlugin, interactionPlugin]}
						initialView="dayGridFourWeek"
						views={{
							dayGridFourWeek: {
								type: 'dayGrid',
								duration: { months: 2 },
							},
						}}
						locale={localeMap[i18n.resolvedLanguage]}
						firstDay={1}
						weekends
						editable
						selectable
						dateClick={handleDateClick}
						eventClick={handleEventClick}
						eventDrop={handleEventDropResize}
						eventResize={handleEventDropResize}
						select={handleDateRangeSelect}
						headerToolbar={{ left: '', center: '', right: '' }}
						slotMinTime="08:00:00"
						slotMaxTime="16:00:00"
						events={events}
						datesSet={handleDatesSet} // called when date range changes
						weekNumbers
						weekNumberClassNames="text-color-blue-500 fw-bold background-none ml-1"
						dayHeaderFormat={{ weekday: 'long' }}
						dayHeaderClassNames={(date) => {
							const isToday =
								date.dow === DateTime.now().weekday &&
								DateTime.fromJSDate(date.view.getCurrentData().currentDate).month === DateTime.now().month
							const todayClass = isToday ? 'fc-current' : ''
							return `${todayClass} py-3`
						}}
						dayCellClassNames={(arg) => {
							if (!arg?.date) return ''
							const day = DateTime.fromJSDate(arg.date).startOf('day')

							const holiday = getCompanyCommonHoliday(arg.date) // palauttaa objektin tai undefined
							const isHoliday = !!holiday

							const isWeekend = day.weekday === 6 || day.weekday === 7

							const hasEvent = events.some((ev) => {
								const start = DateTime.fromISO(ev.start).startOf('day')
								const end = DateTime.fromISO(ev.end).endOf('day')
								return day >= start && day <= end
							})

							const classes = []
							if (isHoliday) classes.push('fc-daygrid-holiday')
							if (isWeekend) classes.push('weekend')
							if (hasEvent) classes.push('has-event')
							if ((isHoliday || isWeekend) && hasEvent) classes.push('specialdate-with-event')

							return classes.join(' ')
						}}
						dayCellContent={(date) => {
							return <CalendarCellDate date={date} event={getCompanyCommonHoliday(date.date)} />
						}}
						fixedWeekCount={false}
						showNonCurrentDates={false}
						themeSystem="bootstrap"
						eventContent={(event) => {
							const eventData = events?.find(({ id }) => id.toString() === event.event.id)
							return eventData ? (
								<TimelineEvent
									event={eventData}
									calendarViewStartDate={calendarViewStartDate}
									calendarViewEndDate={calendarViewEndDate}
									disableButtons
								/>
							) : null
						}}
						eventDataTransform={(event) => ({
							...event,
							end: DateTime.fromISO(event.end).plus({ days: 1 }).toFormat('yyyy-MM-dd'),
						})}
					/>
				</PersonalCalendarWrapper>
			</PageContent>
		</>
	)
}
